import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth';
import { haptic } from '../../lib/haptic';

type Props = { onClose: () => void };

type StackItem = {
  to: string;
  labelKey: string;
  icon: JSX.Element;
};

const HomeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9.5L12 2l9 7.5V21a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const TodayIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const HistoryIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const VehicleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="11" width="20" height="7" rx="1" />
    <path d="M5 11V8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v3" />
    <circle cx="7" cy="18" r="1.5" />
    <circle cx="17" cy="18" r="1.5" />
  </svg>
);
const ProfileIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const LanguageIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const HelpIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export function MenuStack({ onClose }: Props) {
  const { t } = useTranslation('menu');
  const signOut = useAuth(s => s.signOut);

  const items: StackItem[] = [
    { to: '/', labelKey: 'home', icon: HomeIcon },
    { to: '/today', labelKey: 'today', icon: TodayIcon },
    { to: '/trips/history', labelKey: 'history', icon: HistoryIcon },
    { to: '/vehicle', labelKey: 'vehicle', icon: VehicleIcon },
    { to: '/profile', labelKey: 'profile', icon: ProfileIcon },
    { to: '/profile/language', labelKey: 'language', icon: LanguageIcon },
    { to: '/help', labelKey: 'help', icon: HelpIcon },
  ];

  return (
    <nav
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px',
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.10)',
        borderRadius: '18px',
        boxShadow: '0 14px 32px rgba(0, 0, 0, 0.30), 0 2px 6px rgba(0, 0, 0, 0.18)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
      }}
      aria-label="Main menu"
    >
      {items.map(it => (
        <Link
          key={it.to}
          to={it.to}
          onClick={() => { haptic.select(); onClose(); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            borderRadius: '12px',
            color: 'rgba(255, 255, 255, 0.92)',
            textDecoration: 'none',
            minHeight: 'var(--touch-target)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.10)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ width: '18px', height: '18px', display: 'block' }}>
              {it.icon}
            </span>
          </span>
          <span
            style={{
              flex: 1,
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: 'rgba(255, 255, 255, 0.92)',
            }}
          >
            {t(it.labelKey)}
          </span>
        </Link>
      ))}
      <button
        type="button"
        onClick={async () => {
          haptic.select();
          await signOut();
          onClose();
        }}
        style={{
          marginTop: '8px',
          padding: '12px',
          borderRadius: '12px',
          border: '1.5px solid rgba(255, 255, 255, 0.22)',
          background: 'transparent',
          color: 'rgba(255, 255, 255, 0.75)',
          fontFamily: 'inherit',
          fontSize: '14px',
          fontWeight: 700,
          letterSpacing: '0.01em',
          cursor: 'pointer',
          minHeight: 'var(--touch-target)',
          textAlign: 'center',
        }}
      >
        {t('signOut')}
      </button>
    </nav>
  );
}
